import * as React from "react";
import { TextField } from "@mui/material";
import {
  AsYouTypeFormatter,
  PhoneNumberFormat,
  PhoneNumberUtil,
} from "google-libphonenumber";

const DEFAULT_COUNTRY = "US";

const phoneUtil = PhoneNumberUtil.getInstance();

/**
 * Format digits using Google's AsYouTypeFormatter (digit-by-digit).
 * Avoids re-inserting ")" or "(" on backspace.
 */
function formatDigitsAsYouType(digits, regionCode) {
  if (!digits || !regionCode) return "";
  const formatter = new AsYouTypeFormatter(regionCode);
  let result = "";
  for (let i = 0; i < digits.length; i++) {
    result = formatter.inputDigit(digits[i]);
  }
  return result;
}

/**
 * Format a raw value (E.164 or partial) for display.
 * When nationalOnly=true, uses NATIONAL format (no country code prefix).
 */
function formatDisplay(raw, defaultCountry, nationalOnly = false) {
  if (!raw || typeof raw !== "string") return "";
  const trimmed = raw.trim();
  if (!trimmed) return "";
  try {
    const number = phoneUtil.parse(trimmed, defaultCountry);
    if (phoneUtil.isValidNumber(number)) {
      return phoneUtil.format(
        number,
        nationalOnly ? PhoneNumberFormat.NATIONAL : PhoneNumberFormat.INTERNATIONAL,
      );
    }
  } catch (_) {
    // invalid or partial
  }
  const digits = trimmed.replace(/\D/g, "");
  if (!digits.length) return trimmed;
  return formatDigitsAsYouType(digits, defaultCountry);
}

/**
 * Formats a phone string for display.
 * Value is stored and accepted as E.164; display is formatted.
 * Uses Google's libphonenumber (digit-by-digit AsYouTypeFormatter) so backspace works through ")" and "(".
 *
 * @param {string}   value          E.164 or partial phone string
 * @param {function} onChange       Called with E.164 string when valid, or raw digits
 * @param {string}   defaultCountry Default country for parsing (default: 'US')
 * @param {boolean}  nationalOnly   When true, display omits the country code prefix
 * @param {object}   ...            Other TextField props
 */
export default function PhoneTextField({
  value = "",
  onChange,
  defaultCountry = DEFAULT_COUNTRY,
  nationalOnly = false,
  ...textFieldProps
}) {
  const [displayValue, setDisplayValue] = React.useState(() =>
    formatDisplay(value, defaultCountry, nationalOnly),
  );
  const isControlled = value !== undefined && value !== null;

  React.useEffect(() => {
    if (isControlled) {
      setDisplayValue(formatDisplay(value, defaultCountry, nationalOnly));
    }
  }, [value, defaultCountry, isControlled, nationalOnly]);

  const handleChange = (e) => {
    const input = e.target.value;
    const digits = input.replace(/\D/g, "");
    const formatted = formatDigitsAsYouType(digits, defaultCountry);
    setDisplayValue(formatted);
    if (!digits) {
      onChange?.("");
      return;
    }
    try {
      const number = phoneUtil.parse(
        input.startsWith("+") ? "+" + digits : digits,
        defaultCountry,
      );
      if (phoneUtil.isValidNumber(number)) {
        onChange?.(phoneUtil.format(number, PhoneNumberFormat.E164));
      } else {
        onChange?.(input.startsWith("+") ? input : digits);
      }
    } catch (_) {
      onChange?.(input.startsWith("+") ? input : digits);
    }
  };

  const defaultPlaceholder = nationalOnly ? "" : "+1 (555) 123-4567";

  return (
    <TextField
      {...textFieldProps}
      value={displayValue}
      onChange={handleChange}
      placeholder={textFieldProps.placeholder ?? defaultPlaceholder}
      inputProps={{
        ...textFieldProps.inputProps,
        inputMode: "tel",
        autoComplete: "tel",
      }}
    />
  );
}
